// External
import React, { useEffect, useRef, useState } from "react";
import styled, { css, keyframes } from "styled-components";
import { rgba } from "polished";
import { breakpoints, fontFamily, colors } from "../../../theme";
import { PageSection } from "../../components/layout/layout-styles";
import { interactionClassNames } from "../../styles/global-styles";
import { usernames } from "../../../config";
import Button from "../../components/form/Button";
import SplashText from "../../components/SplashText";
import { useForm, useFormState } from "react-hook-form";
import toast from "react-hot-toast";

// Assets
import paperPlaneIcon from "../../images/paper-plane.svg";
import copyIcon from "../../images/copy.svg";
import paperClipIcon from "../../images/paper-clip.svg";
import errorWarningIcon from "../../images/error-warning.svg";
import fileImageIcon from "../../images/file-image.svg";
import fileDocIcon from "../../images/file-doc.svg";
import deleteIcon from "../../images/delete.svg";
import lightningIcon from "../../images/lightning.svg";
import { copyText } from "../../utils/helpers/clipboard";

const CONTACT_EMAIL = usernames.email;
const CONTACT_API_URL = process.env.GATSBY_CONTACT_API_URL || "";
const MAX_FILES = 5;
const MAX_FILE_SIZE = 5 * 1024 * 1024;
const ALLOWED_MIME_TYPES = new Set([
  "application/pdf",
  "image/jpeg",
  "image/webp",
  "image/png",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
]);

const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
const NAME_MIN_LENGTH = 2;
const NAME_MAX_LENGTH = 50;
/** Unicode letters/marks, spaces, hyphens, apostrophes, periods (initials). */
const NAME_REGEX =
  /^(?:[\p{L}\p{M}]+(?:['\u2019.\-][\p{L}\p{M}]+)*\.?)(?: (?:[\p{L}\p{M}]+(?:['\u2019.\-][\p{L}\p{M}]+)*\.?))*$/u;

const normalizeName = (name) =>
  name
    .normalize("NFC")
    .trim()
    .replace(/\s+/g, " ")
    .replace(/[\u2018\u2019]/g, "'");

const isValidName = (name) => {
  const normalized = normalizeName(name || "");
  if (
    normalized.length < NAME_MIN_LENGTH ||
    normalized.length > NAME_MAX_LENGTH
  ) {
    return false;
  }
  return NAME_REGEX.test(normalized);
};

const validateEmail = (value) => {
  if (!value?.trim()) return "Email is required.";
  return EMAIL_REGEX.test(value.trim()) || "Please enter a valid email.";
};

const validateName = (value) => {
  if (!value?.trim()) return "Name is required.";
  return isValidName(value) || "Please enter a valid name.";
};

const validateOptionalJobTitle = (value) => {
  const trimmed = (value || "").trim();
  if (!trimmed) return true;
  if (trimmed.length < 2 || trimmed.length > 100) {
    return "Job title must be 2-100 characters.";
  }
  return true;
};

const isAllowedFile = (file) =>
  ALLOWED_MIME_TYPES.has(file.type) && file.size <= MAX_FILE_SIZE;

const isImageFile = (file) => file.type.startsWith("image/");

// Styles
const StyledButton = styled(Button)`
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 16px;
  padding: 12px 16px;
  border-radius: 18px;

  @media (min-width: ${breakpoints.tablet}) {
    font-size: 20px;
    padding: 18px 30px;
  }
`;

const ContactUsButton = styled(StyledButton)`
  margin-left: 0;
  width: max-content;
  margin-top: 14px;
`;

const sharedInputStyles = css`
  width: 100%;
  border-radius: 6px;
  padding: 8px;
  box-sizing: border-box;

  border: 1px solid
    ${({ $error }) => ($error ? colors.form.error : colors.form.border)};
  box-shadow: ${({ $error }) =>
    $error ? `0 0 0 1px ${colors.form.error}` : "none"};
  outline: none;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;

  &,
  &::placeholder {
    font-size: 16px;
    font-weight: 700;
    line-height: 1.02;
  }

  &:hover,
  &:focus-visible {
    border-color: ${({ $error }) =>
      $error ? colors.form.error : colors.form.borderHover};
    box-shadow: ${({ $error }) =>
      $error ? `0 0 0 1px ${colors.form.error}` : "none"};
  }

  @media only screen and (min-width: ${breakpoints.tablet}) {
    &,
    &::placeholder {
      font-size: 20px;
    }
  }
`;

const SectionContainer = styled(PageSection)`
  width: 100%;
`;

const Section = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  grid-template-rows: auto auto;
  gap: 42px;
  width: 94%;
  max-width: 1040px;
  margin: 0 auto;
  box-sizing: border-box;

  @media only screen and (min-width: ${breakpoints.desktop}) {
    grid-template-columns: 1fr 1fr;
    grid-template-rows: auto 1fr auto auto;
    column-gap: 88px;
    row-gap: 42px;
  }
`;

const TitleSection = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  text-align: center;

  @media only screen and (min-width: ${breakpoints.desktop}) {
    grid-column: 1;
    grid-row: 1;
    text-align: start;
  }
`;

const Heading = styled.h1`
  display: grid;
  margin: 0;
  font-family: ${fontFamily.extraBold};
  font-size: 32px;
  line-height: 1.02;

  @media only screen and (min-width: ${breakpoints.desktop}) {
    font-size: 40px;
  }
`;

const HeadingLine = styled.span`
  display: block;
`;

const HeadingLineWithIcon = styled.span`
  display: flex;
  align-items: baseline;
  justify-content: center;
  flex-wrap: wrap;
  gap: 8px;

  @media only screen and (min-width: ${breakpoints.desktop}) {
    justify-content: flex-start;
  }
`;

const PaperPlaneWrap = styled.span`
  position: relative;
  height: 44px;

  @media only screen and (min-width: ${breakpoints.desktop}) {
    height: 52px;
  }
`;

const PaperPlaneIcon = styled.img`
  position: absolute;
  top: 16px;
  left: 8px;
  height: 100%;
  width: auto;
  display: block;
`;

const Subtitle = styled.p`
  margin: 20px 0 0;
  padding: 0 16px;
  font-size: 20px;
  line-height: 1.2;
  font-family: ${fontFamily.medium};
  font-weight: 500;

  @media only screen and (min-width: ${breakpoints.desktop}) {
    padding: 0;
  }
`;

const AvailabilityBadge = styled.span`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 11px 17px;
  border-radius: 999px;
  background-color: ${colors.availability.background};
  border: 1px solid ${colors.availability.content};
  color: ${colors.availability.content};
  font-family: ${fontFamily.bold};
  font-size: 16px;
  line-height: 1;
  margin: 20px auto 0;
  max-width: max-content;

  @media only screen and (min-width: ${breakpoints.desktop}) {
    font-size: 17px;
    margin: 16px 0 0;
  }
`;

const BoltIcon = styled.span`
  width: 21px;
  height: 21px;
  background-color: ${colors.availability.content};
  mask: url(${lightningIcon}) center / contain no-repeat;
  -webkit-mask: url(${lightningIcon}) center / contain no-repeat;
`;

const AdditionalInfo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;

  @media only screen and (min-width: ${breakpoints.desktop}) {
    grid-column: 1;
    grid-row: 3;
    align-self: end;
    justify-self: start;
    align-items: flex-start;
    text-align: start;
  }
`;

const OrHeading = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  font-family: ${fontFamily.bold};
  font-size: 20px;
  line-height: 102%;

  p {
    margin: 0;
  }

  @media only screen and (min-width: ${breakpoints.desktop}) {
    font-size: 24px;
  }
`;

const EmailRow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 12px;
  color: ${colors.accentContent};
`;

const EmailLink = styled.a.attrs({
  className: interactionClassNames.textAction,
})`
  font-size: 16px;
  font-weight: 500;
  line-height: 102%;
  color: inherit;
  text-decoration: none;

  @media only screen and (min-width: ${breakpoints.desktop}) {
    font-size: 18px;
  }
`;

const CopyButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  padding: 0;
  border: none;
  border-radius: 8px;
  background: transparent;
  cursor: pointer;
  transition: background-color 0.2s ease, backdrop-filter 0.2s ease;
  &:hover {
    background-color: ${rgba(colors.clickIcon, 0.2)};
    backdrop-filter: blur(12px);
  }

  &:active {
    background-color: ${rgba(colors.clickIcon, 0.4)};
  }

  img {
    width: 24px;
    height: 24px;
    display: block;
  }
`;

const Form = styled.form`
  margin: 0 auto;
  font-family: ${fontFamily.medium};
  width: 100%;
  max-width: 532px;
  border-radius: 20px;
  background: ${colors.form.cardBackground};
  border: 1px solid ${colors.code.border};
  padding: 24px;
  box-sizing: border-box;

  @media only screen and (min-width: ${breakpoints.desktop}) {
    grid-column: 2;
    grid-row: 1 / span 3;
    justify-self: start;
    margin: 0;
    padding: 32px;
  }
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 9px;

  @media only screen and (min-width: ${breakpoints.tablet}) {
    gap: 16px;
  }
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 500;

  @media only screen and (min-width: ${breakpoints.tablet}) {
    font-size: 16px;
  }
`;

const Required = styled.span`
  color: ${colors.form.error};
`;

const InputWrapper = styled.div`
  position: relative;
`;

const Input = styled.input`
  ${sharedInputStyles}
`;

const Textarea = styled.textarea`
  ${sharedInputStyles}
  resize: none;
`;

const ErrorIcon = styled.img`
  position: absolute;
  top: 13px;
  right: 13px;
  height: 18px;
  width: 18px;
  display: block;
`;

const errorShake = keyframes`
  0%,
  100% {
    transform: translateX(0);
  }
  20%,
  60% {
    transform: translateX(-4px);
  }
  40%,
  80% {
    transform: translateX(4px);
  }
`;

const ErrorText = styled.span`
  display: block;
  min-height: 16px;
  font-size: 12px;
  font-weight: 500;
  line-height: 1.2;
  color: ${colors.form.error};

  ${({ $shake }) =>
    $shake &&
    css`
      animation: ${errorShake} 0.45s ease;
    `}
`;

const FileIconContainer = styled.div`
  display: flex;
  height: 34px;
  width: 34px;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  border: 1px solid ${colors.code.border};
  background: ${colors.form.inputBackground};
  color: ${colors.grey.darkMedium};

  img {
    width: 18px;
    height: 20px;
    display: block;
  }
`;

const AttachmentDropzone = styled.label`
  display: flex;
  width: max-content;
  max-width: 100%;
  align-items: center;
  gap: 4px;
  cursor: pointer;

  &:hover ${FileIconContainer} {
    background: ${colors.accentLight};
  }
`;

const HiddenFileInput = styled.input`
  display: none;
`;

const AttachmentLabel = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 4px;
  font-size: 14px;
  font-weight: 500;

  @media only screen and (min-width: ${breakpoints.tablet}) {
    font-size: 16px;
  }
`;

const FileList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-top: 8px;
  max-height: 110px;
  overflow-y: auto;
`;

const FileCard = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  height: 40px;
  box-sizing: border-box;
  padding: 4px 12px;
  overflow: hidden;
  border: 1px solid ${colors.code.border};
  border-radius: 4px;
  background: ${colors.form.inputBackground};
  color: ${colors.grey.darkMedium};
`;

const FileTypeIconChip = styled.div`
  display: flex;
  height: 32px;
  width: 32px;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  background: ${colors.accentLight};
`;

const FileTypeIcon = styled.img`
  width: 16px;
  height: 16px;
  display: block;
`;

const FileName = styled.span`
  flex: 1;
  min-width: 0;
  padding-inline: 4px;
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: ${colors.grey.darkMedium};
`;

const RemoveFileButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: auto;
  width: 16px;
  height: 16px;
  padding: 0;
  border: none;
  background: transparent;
  color: ${colors.grey.darkMedium};
  cursor: pointer;
  transition: color 0.2s ease;

  &::before {
    content: "";
    display: block;
    width: 16px;
    height: 16px;
    background-color: currentColor;
    mask: url(${deleteIcon}) center / contain no-repeat;
    -webkit-mask: url(${deleteIcon}) center / contain no-repeat;
  }

  &:hover:not(:disabled),
  &:focus-visible:not(:disabled) {
    color: ${colors.form.error};
  }
`;

const FileInfo = styled.span`
  font-size: 12px;
  font-weight: 500;
  line-height: 102%;
  color: ${colors.grey.darkMedium};
`;

const ResponseNote = styled.p`
  margin: 0;
  grid-column: 1;
  text-align: center;
  font-family: ${fontFamily.bold};
  font-size: 16px;
  line-height: 102%;
  color: ${colors.linkHover.light};

  @media only screen and (min-width: ${breakpoints.desktop}) {
    grid-column: 1 / -1;
    grid-row: 4;
    font-size: 20px;
  }
`;

const AsyncButton = ({ state, children, ...props }) => {
  return (
    <ContactUsButton {...props}>
      {state === "loading" && <>Submitting...</>}
      {state === "success" && <>Success...</>}
      {state === "error" && <>Error...</>}
      {!state && children}
    </ContactUsButton>
  );
};

const ContactMeSection = () => {
  const [formStatus, setFormStatus] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [errorShakeKey, setErrorShakeKey] = useState(0);
  const fileInputRef = useRef(null);

  const { register, handleSubmit, control, reset, clearErrors } = useForm({
    mode: "onBlur",
    reValidateMode: "onBlur",
    shouldFocusError: false,
    defaultValues: {
      name: "",
      company: "",
      job_title: "",
      email: "",
      message: "",
    },
  });

  // Dedicated subscription - more reliable than destructuring formState from useForm
  const { errors } = useFormState({ control });

  const isBusy = formStatus === "loading";

  const shakeErrorMessages = () => {
    setErrorShakeKey((key) => key + 1);
  };

  // Clear error while typing; validate when the field loses focus (mode: onBlur)
  const registerField = (name, rules = {}) =>
    register(name, {
      ...rules,
      onChange: () => clearErrors(name),
    });

  useEffect(() => {
    if (formStatus === "success") {
      setTimeout(function () {
        setFormStatus("");
        reset({
          name: "",
          company: "",
          job_title: "",
          email: "",
          message: "",
        });
        setAttachments([]);
        setErrorShakeKey(0);
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
      }, 1500);
      return;
    }

    if (formStatus === "error") {
      setTimeout(function () {
        setFormStatus("");
      }, 1500);
    }
  }, [formStatus, reset]);

  const handleCopyEmail = async () => {
    try {
      await copyText(CONTACT_EMAIL);
      toast.success("Email copied");
    } catch (error) {
      toast.error("Could not copy email");
    }
  };

  const handleAttachmentChange = (event) => {
    const incoming = Array.from(event.target.files || []);
    if (!incoming.length) return;

    setAttachments((prev) => {
      const slotsLeft = MAX_FILES - prev.length;
      let rejectedCount = 0;
      const next = [...prev];

      incoming.slice(0, slotsLeft).forEach((file) => {
        if (!isAllowedFile(file)) {
          rejectedCount += 1;
          return;
        }
        next.push(file);
      });

      if (rejectedCount > 0) {
        toast.error(
          rejectedCount === 1
            ? "1 file was rejected. Please check the accepted file types."
            : `${rejectedCount} files were rejected. Please check the accepted file types.`,
        );
      } else if (incoming.length > slotsLeft) {
        toast.error("Maximum 5 files allowed.");
      }

      return next;
    });

    event.target.value = "";
  };

  const handleRemoveAttachment = (index) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  const handleInvalidSubmit = () => {
    shakeErrorMessages();
  };

  const handleFormSubmit = (data) => {
    if (isBusy) return;

    setFormStatus("loading");

    const formData = new FormData();
    ["name", "company", "job_title", "email", "message"].forEach((key) => {
      formData.append(key, data[key] ?? "");
    });
    formData.append("source", "personal-website");
    attachments.forEach((file) => {
      formData.append("attachment", file);
    });

    toast.promise(
      fetch(CONTACT_API_URL, {
        method: "POST",
        body: formData,
      }).then(async (response) => {
        if (!response.ok) {
          throw new Error("Submission failed");
        }

        return response;
      }),
      {
        loading: "Submitting...",
        success: () => {
          setFormStatus("success");
          return <b>Thanks! Your message has been sent.</b>;
        },
        error: () => {
          setFormStatus("error");
          return (
            <span>
              Sorry for the inconvenience. We couldn&apos;t process your request
              at the moment. Please contact us at{" "}
              <a
                href={`mailto:${CONTACT_EMAIL}`}
                style={{
                  color: "inherit",
                  textDecoration: "underline",
                  fontWeight: 700,
                }}
              >
                {CONTACT_EMAIL}
              </a>{" "}
              if the issue persists.
            </span>
          );
        },
      },
      {
        error: { duration: 8000 },
      },
    );
  };

  return (
    <SectionContainer
      name="contactMe"
      as="section"
      $bgColor={colors.accentLight}
    >
      <Section>
        <TitleSection>
          <Heading>
            <HeadingLine>Let's bring your next</HeadingLine>
            <HeadingLineWithIcon>
              Big Idea to life
              <PaperPlaneWrap aria-hidden="true">
                <PaperPlaneIcon src={paperPlaneIcon} alt="" />
              </PaperPlaneWrap>
            </HeadingLineWithIcon>
          </Heading>
          <Subtitle>
            Whether you're brainstorming ideas, looking to bolster capabilities,
            rapidly expanding your team, or delegating projects, count on us to
            meet your needs effectively any or every step of the way
          </Subtitle>

          <AvailabilityBadge>
            <BoltIcon aria-hidden="true" />
            Available for new engagements
          </AvailabilityBadge>
        </TitleSection>

        <Form
          onSubmit={handleSubmit(handleFormSubmit, handleInvalidSubmit)}
          noValidate
        >
          <FieldGroup>
            {[
              {
                name: "name",
                label: "Name",
                required: true,
                maxLength: 100,
                rules: {
                  required: "Name is required.",
                  validate: validateName,
                },
              },
              {
                name: "company",
                label: "Company",
                required: false,
                maxLength: 100,
                rules: {},
              },
              {
                name: "job_title",
                label: "Job Title",
                required: false,
                maxLength: 100,
                rules: { validate: validateOptionalJobTitle },
              },
            ].map(({ name, label, required, maxLength, rules }) => (
              <Field key={name}>
                <Label>
                  {label}
                  {required && (
                    <>
                      {" "}
                      <Required>*</Required>
                    </>
                  )}
                </Label>
                <InputWrapper>
                  <Input
                    $error={!!errors[name]}
                    maxLength={maxLength}
                    aria-invalid={errors[name] ? "true" : "false"}
                    {...registerField(name, rules)}
                  />
                  {errors[name] && (
                    <ErrorIcon
                      src={errorWarningIcon}
                      alt=""
                      aria-hidden="true"
                    />
                  )}
                </InputWrapper>
                <ErrorText
                  role={errors[name]?.message ? "alert" : undefined}
                  key={
                    errors[name]?.message
                      ? `${name}-error-${errorShakeKey}`
                      : `${name}-ok`
                  }
                  $shake={!!errors[name]?.message && errorShakeKey > 0}
                >
                  {errors[name]?.message || "\u00A0"}
                </ErrorText>
              </Field>
            ))}

            <Field>
              <Label>
                Email Address <Required>*</Required>
              </Label>
              <InputWrapper>
                <Input
                  type="email"
                  $error={!!errors.email}
                  aria-invalid={errors.email ? "true" : "false"}
                  {...registerField("email", {
                    validate: validateEmail,
                  })}
                />
                {errors.email && (
                  <ErrorIcon src={errorWarningIcon} alt="" aria-hidden="true" />
                )}
              </InputWrapper>
              <ErrorText
                role={errors.email?.message ? "alert" : undefined}
                key={
                  errors.email?.message
                    ? `email-error-${errorShakeKey}`
                    : "email-ok"
                }
                $shake={!!errors.email?.message && errorShakeKey > 0}
              >
                {errors.email?.message || "\u00A0"}
              </ErrorText>
            </Field>

            <Field>
              <Label>How can we help you?</Label>
              <Textarea
                rows={5}
                $error={!!errors.message}
                aria-invalid={errors.message ? "true" : "false"}
                {...registerField("message", {
                  maxLength: { value: 1000, message: "Message too long." },
                })}
              />
              <ErrorText
                role={errors.message?.message ? "alert" : undefined}
                key={
                  errors.message?.message
                    ? `message-error-${errorShakeKey}`
                    : "message-ok"
                }
                $shake={!!errors.message?.message && errorShakeKey > 0}
              >
                {errors.message?.message || "\u00A0"}
              </ErrorText>
            </Field>

            <Field>
              <AttachmentDropzone htmlFor="form-attachment">
                <HiddenFileInput
                  ref={fileInputRef}
                  id="form-attachment"
                  name="attachment"
                  type="file"
                  multiple
                  accept=".pdf,.jpg,.jpeg,.webp,.png,.doc,.docx,application/pdf,image/jpeg,image/webp,image/png,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                  onChange={handleAttachmentChange}
                />
                <FileIconContainer>
                  <img src={paperClipIcon} alt="" aria-hidden="true" />
                </FileIconContainer>
                <AttachmentLabel>
                  <span>Add an Attachment (Optional)</span>
                  <FileInfo>
                    Accepted: PDF, JPG, WEBP, PNG, DOC, DOCX (max 5MB, up to 5
                    files)
                  </FileInfo>
                </AttachmentLabel>
              </AttachmentDropzone>
              {attachments.length > 0 && (
                <FileList>
                  {attachments.map((file, index) => (
                    <FileCard key={`${file.name}-${index}`}>
                      <FileTypeIconChip>
                        <FileTypeIcon
                          src={isImageFile(file) ? fileImageIcon : fileDocIcon}
                          alt=""
                          aria-hidden="true"
                        />
                      </FileTypeIconChip>
                      <FileName title={file.name}>{file.name}</FileName>
                      <RemoveFileButton
                        type="button"
                        aria-label={`Remove ${file.name}`}
                        onClick={() => handleRemoveAttachment(index)}
                      />
                    </FileCard>
                  ))}
                </FileList>
              )}
            </Field>

            <AsyncButton type="submit" disabled={isBusy} state={formStatus}>
              Submit Form
            </AsyncButton>
          </FieldGroup>
        </Form>

        <AdditionalInfo>
          <OrHeading>
            <p>OR</p>
            <p>Write To Us At Your Own Convenience</p>
          </OrHeading>
          <EmailRow>
            <EmailLink href={`mailto:${CONTACT_EMAIL}`}>
              {CONTACT_EMAIL}
            </EmailLink>
            <CopyButton
              type="button"
              aria-label="Copy email address"
              onClick={handleCopyEmail}
            >
              <img src={copyIcon} alt="" aria-hidden="true" />
            </CopyButton>
          </EmailRow>
        </AdditionalInfo>

        <ResponseNote>
          You can expect a response from us within 24 hours
        </ResponseNote>
      </Section>
    </SectionContainer>
  );
};

export default ContactMeSection;
